import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Client } from '../../data/clients';
import { MapPin, Clock, Phone, Mail, Edit } from 'lucide-react';
interface ClientsMapProps {
  clients: Client[];
  onEdit: (client: Client) => void;
}
// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
export function ClientsMap({
  clients,
  onEdit
}: ClientsMapProps) {
  const [mapType, setMapType] = useState<'roadmap' | 'satellite' | 'hybrid'>('hybrid');
  const center: [number, number] = [46.8139, -71.208];
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };
  const createCustomIcon = (priority: string) => {
    const color = priority === 'high' ? '#EF4444' : priority === 'medium' ? '#F59E0B' : '#3B82F6';
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          width: 32px;
          height: 32px;
          background-color: ${color};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });
  };
  const getTileLayer = () => {
    switch (mapType) {
      case 'roadmap':
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '© OpenStreetMap contributors'
        };
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '© Esri'
        };
      case 'hybrid':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '© Esri'
        };
      default:
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '© OpenStreetMap contributors'
        };
    }
  };
  const tileLayer = getTileLayer();
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-91">
      <div className="p-4 border-b border-gray-200" data-id="element-92">
        <div className="flex items-center justify-between" data-id="element-93">
          <div data-id="element-94">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-95">
              Carte des clients
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-96">
              Vue satellite avec bâtiments visibles
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-97">
            <button onClick={() => setMapType('roadmap')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${mapType === 'roadmap' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-98">
              Plan
            </button>
            <button onClick={() => setMapType('satellite')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${mapType === 'satellite' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-99">
              Satellite
            </button>
            <button onClick={() => setMapType('hybrid')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${mapType === 'hybrid' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-100">
              Hybride
            </button>
          </div>
        </div>
      </div>
      <div style={{
      height: '700px',
      width: '100%'
    }} data-id="element-101">
        <MapContainer center={center} zoom={12} style={{
        height: '100%',
        width: '100%'
      }} zoomControl={true} data-id="element-102">
          <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} data-id="element-103" />
          {mapType === 'hybrid' && <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" opacity={0.3} data-id="element-104" />}
          {clients.map(client => <Marker key={client.id} position={[client.address.coordinates.lat, client.address.coordinates.lng]} icon={createCustomIcon(client.priority)} data-id="element-105">
              <Popup data-id="element-106">
                <div className="p-2 min-w-[250px]" data-id="element-107">
                  <div className="flex items-start justify-between mb-2" data-id="element-108">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-109">
                      {client.name}
                    </h3>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${client.priority === 'high' ? 'bg-red-100 text-red-700' : client.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-110">
                      {client.priority === 'high' ? 'Haute' : client.priority === 'medium' ? 'Moyenne' : 'Basse'}
                    </span>
                  </div>
                  <div className="space-y-2 mb-3" data-id="element-111">
                    <div className="flex items-start space-x-2 text-xs" data-id="element-112">
                      <MapPin className="w-3 h-3 text-gray-400 mt-0.5" data-id="element-113" />
                      <div className="text-gray-600" data-id="element-114">
                        {client.address.street}
                        <br data-id="element-115" />
                        {client.address.city}, {client.address.postalCode}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-xs" data-id="element-116">
                      <Clock className="w-3 h-3 text-blue-600" data-id="element-117" />
                      <span className="text-gray-900 font-semibold" data-id="element-118">
                        Durée: {formatDuration(client.snowRemovalDuration)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-600" data-id="element-119">
                      <Phone className="w-3 h-3" data-id="element-120" />
                      <span data-id="element-121">{client.contact.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-600" data-id="element-122">
                      <Mail className="w-3 h-3" data-id="element-123" />
                      <span data-id="element-124">{client.contact.email}</span>
                    </div>
                  </div>
                  <button onClick={() => onEdit(client)} className="w-full flex items-center justify-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium" data-id="element-125">
                    <Edit className="w-3 h-3" data-id="element-126" />
                    <span data-id="element-127">Modifier</span>
                  </button>
                </div>
              </Popup>
            </Marker>)}
        </MapContainer>
      </div>
      {/* Legend */}
      <div className="p-4 border-t border-gray-200 bg-gray-50" data-id="element-128">
        <div className="flex items-center justify-between" data-id="element-129">
          <div className="flex items-center space-x-6" data-id="element-130">
            <div className="flex items-center space-x-2" data-id="element-131">
              <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white" data-id="element-132"></div>
              <span className="text-xs text-gray-700" data-id="element-133">Priorité haute</span>
            </div>
            <div className="flex items-center space-x-2" data-id="element-134">
              <div className="w-4 h-4 bg-yellow-500 rounded-full border-2 border-white" data-id="element-135"></div>
              <span className="text-xs text-gray-700" data-id="element-136">Priorité moyenne</span>
            </div>
            <div className="flex items-center space-x-2" data-id="element-137">
              <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white" data-id="element-138"></div>
              <span className="text-xs text-gray-700" data-id="element-139">Priorité basse</span>
            </div>
          </div>
          <p className="text-xs text-gray-500" data-id="element-140">
            Cliquez sur un marqueur pour voir les détails
          </p>
        </div>
      </div>
    </div>;
}